import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Sparkles, Save, X, Loader2, FlaskConical, Leaf, Bug } from "lucide-react";
import api from "../../lib/api";

/**
 * Structured detail data editor for a plant's A4 profile PDF.
 * Loads /api/admin/plants/{id}/profile-data, offers Claude generation
 * ("KI ergänzen") and manual editing per section, saves via PUT.
 */

const SECTIONS = [
  {
    key: "basics",
    icon: Leaf,
    title: "Steckbrief-Basisdaten",
    fields: [
      { path: "family_lat", label: "Wiss. Familie", placeholder: "z. B. Urticaceae" },
      { path: "family_de", label: "Familie (Deutsch)", placeholder: "z. B. Brennnesselgewächse" },
      { path: "alt_names", label: "Alternative Namen", placeholder: "kommagetrennt" },
      { path: "height_cm", label: "Wuchshöhe", placeholder: "z. B. 30–150 cm" },
      { path: "lifespan", label: "Lebensdauer", placeholder: "ein-/mehrjährig" },
      { path: "location", label: "Standort", placeholder: "" },
      { path: "soil", label: "Boden", placeholder: "" },
      { path: "light", label: "Lichtbedarf", placeholder: "Sonne / Halbschatten / Schatten" },
      { path: "distribution", label: "Verbreitung", placeholder: "" },
      { path: "protection_status", label: "Naturschutzstatus", placeholder: "leer wenn keiner" },
      { path: "bloom_time", label: "Blütezeit", placeholder: "z. B. Mai–September" },
      { path: "harvest_time", label: "Sammelzeit", placeholder: "" },
    ],
  },
  {
    key: "identification",
    icon: FlaskConical,
    title: "Bestimmungsmerkmale",
    prefix: "identification",
    fields: [
      { path: "leaf_shape", label: "Blattform" },
      { path: "leaf_color", label: "Blattfarbe" },
      { path: "leaf_arrangement", label: "Blattstellung" },
      { path: "leaf_margin", label: "Blattrand" },
      { path: "leaf_veins", label: "Blattadern" },
      { path: "leaf_top", label: "Oberseite" },
      { path: "leaf_bottom", label: "Unterseite" },
      { path: "leaf_hair", label: "Behaarung Blatt" },
      { path: "flower_color", label: "Blütenfarbe" },
      { path: "flower_shape", label: "Blütenform" },
      { path: "petal_count", label: "Blütenblätter" },
      { path: "inflorescence", label: "Blütenstand" },
      { path: "scent", label: "Duft" },
      { path: "stem_shape", label: "Stängel Form" },
      { path: "stem_hair", label: "Stängel Behaarung" },
      { path: "stem_hollow", label: "Hohl/massiv" },
      { path: "stem_color", label: "Stängelfarbe" },
      { path: "root_type", label: "Wurzeltyp" },
      { path: "fruit_shape", label: "Frucht Form" },
      { path: "fruit_ripening", label: "Reifezeit Frucht" },
      { path: "smell_crushed", label: "Geruch zerrieben" },
      { path: "taste_note", label: "Geschmack" },
    ],
  },
  {
    key: "medicinal",
    icon: FlaskConical,
    title: "Heilpflanzen-Wissen",
    prefix: "medicinal",
    fields: [
      { path: "used_parts", label: "Verwendete Teile" },
      { path: "active_compounds", label: "Inhaltsstoffe" },
      { path: "traditional_uses", label: "Traditionelle Nutzung", multiline: true },
      { path: "modern_findings", label: "Moderne Erkenntnisse", multiline: true },
      { path: "preparations", label: "Anwendungsformen" },
      { path: "dosage_note", label: "Dosierung", multiline: true },
      { path: "sourcing_tips", label: "Sammelhinweise", multiline: true },
      { path: "storage", label: "Lagerung" },
      { path: "shelf_life", label: "Haltbarkeit" },
    ],
  },
  {
    key: "ecology",
    icon: Bug,
    title: "Naturwissen & Ökologie",
    prefix: "ecology",
    fields: [
      { path: "importance", label: "Ökologische Bedeutung", multiline: true },
      { path: "beneficial_insects", label: "Insekten" },
      { path: "consumers", label: "Tiere" },
      { path: "native", label: "Heimisch?", type: "boolean" },
      { path: "history", label: "Historie", multiline: true },
      { path: "mythology", label: "Mythologie", multiline: true },
    ],
  },
  {
    key: "sustainable",
    icon: Leaf,
    title: "Nachhaltiges Sammeln",
    fields: [
      { path: "sustainable_collecting", label: "Tipps (kommagetrennt)", multiline: true, placeholder: "Nur wenige Pflanzen, Abstand zu Straßen, ..." },
    ],
  },
];

function getVal(root, prefix, path) {
  const holder = prefix ? (root?.[prefix] || {}) : root;
  return holder?.[path];
}

function setVal(root, prefix, path, value) {
  const next = { ...(root || {}) };
  if (prefix) {
    next[prefix] = { ...(next[prefix] || {}), [path]: value };
  } else {
    next[path] = value;
  }
  return next;
}

export default function ProfileDataButton({ plant, variant = "icon" }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState({ updated_at: null });
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeKey, setActiveKey] = useState("basics");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/plants/${plant.id}/profile-data`);
      setData(r.data.profile_data || {});
      setMeta({ updated_at: r.data.updated_at });
    } catch (err) {
      toast.error("Konnte Detaildaten nicht laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open]);

  const generate = async () => {
    if (!window.confirm("KI-Detaildaten mit Claude neu erzeugen? Bestehende Detaildaten werden überschrieben.")) return;
    setGenerating(true);
    try {
      const r = await api.post(`/admin/plants/${plant.id}/generate-profile-data`);
      setData(r.data.profile_data || {});
      setMeta({ updated_at: r.data.updated_at });
      toast.success("KI-Detaildaten generiert");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generierung fehlgeschlagen");
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/plants/${plant.id}/profile-data`, { profile_data: data || {} });
      toast.success("Detaildaten gespeichert");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const trigger = variant === "text" ? (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium border"
      style={{ borderColor: "var(--wp-border)", backgroundColor: "var(--wp-cream)", color: "var(--wp-forest)" }}
      data-testid={`profile-data-btn-${plant.slug || plant.id}`}
      title="Detaildaten für PDF"
    >
      <FlaskConical className="w-3.5 h-3.5" /> Detaildaten
    </button>
  ) : (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className="px-2 inline-flex"
      style={{ color: "var(--wp-olive)" }}
      title="Detaildaten für Steckbrief"
      data-testid={`profile-data-btn-${plant.slug || plant.id}`}
    >
      <FlaskConical className="w-4 h-4" />
    </button>
  );

  const activeSection = SECTIONS.find((s) => s.key === activeKey);

  return (
    <>
      {trigger}
      {open && createPortal(
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ backgroundColor: "rgba(26,47,28,0.65)" }}
          onClick={() => setOpen(false)}
          data-testid="profile-data-modal"
        >
          <div
            className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 p-4 sm:p-5 border-b" style={{ borderColor: "var(--wp-border)" }}>
              <div className="min-w-0">
                <div className="overline">Detaildaten für A4-Steckbrief</div>
                <div className="font-display text-lg truncate" style={{ color: "var(--wp-olive)" }}>{plant.name_de}</div>
                {meta.updated_at && (
                  <div className="text-[10px] opacity-60">Zuletzt aktualisiert: {new Date(meta.updated_at).toLocaleString("de-DE")}</div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={generate}
                  disabled={generating || loading}
                  className="btn-secondary text-sm !py-2 !px-3"
                  data-testid="profile-data-generate"
                  title="Von Claude Sonnet 4.5 alle Felder befüllen lassen"
                >
                  {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> KI</> : <><Sparkles className="w-4 h-4" /> KI ergänzen</>}
                </button>
                <button
                  onClick={save}
                  disabled={saving || loading}
                  className="btn-primary text-sm !py-2 !px-3"
                  data-testid="profile-data-save"
                >
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Speichere</> : <><Save className="w-4 h-4" /> Speichern</>}
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="w-9 h-9 rounded-full inline-flex items-center justify-center"
                  style={{ backgroundColor: "var(--wp-cream)" }}
                  data-testid="profile-data-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex-1 min-h-0 grid grid-cols-1 sm:grid-cols-[180px_1fr] overflow-hidden">
              <div className="sm:border-r overflow-x-auto sm:overflow-y-auto flex sm:flex-col gap-1 p-2" style={{ borderColor: "var(--wp-border)" }}>
                {SECTIONS.map((s) => {
                  const Ico = s.icon;
                  const isActive = activeKey === s.key;
                  return (
                    <button
                      key={s.key}
                      onClick={() => setActiveKey(s.key)}
                      className="text-left inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap sm:whitespace-normal"
                      style={{
                        backgroundColor: isActive ? "var(--wp-olive)" : "transparent",
                        color: isActive ? "var(--wp-cream)" : "var(--wp-forest)",
                      }}
                      data-testid={`profile-section-${s.key}`}
                    >
                      <Ico className="w-4 h-4 shrink-0" /> {s.title}
                    </button>
                  );
                })}
              </div>

              <div className="p-4 sm:p-5 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center gap-2 text-sm opacity-70">
                    <Loader2 className="w-4 h-4 animate-spin" /> Lade Daten …
                  </div>
                ) : !data || Object.keys(data).length === 0 ? (
                  <div className="text-center py-12 text-sm opacity-70">
                    Noch keine Detaildaten vorhanden.<br/>
                    <button onClick={generate} disabled={generating} className="btn-primary mt-4 justify-center" data-testid="profile-data-generate-empty">
                      {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generiere …</> : <><Sparkles className="w-4 h-4" /> Jetzt mit Claude erzeugen</>}
                    </button>
                    <div className="text-[11px] opacity-60 mt-4 max-w-xs mx-auto">Claude Sonnet 4.5 füllt Bestimmungsmerkmale, Heilwissen und Ökologie in einem Rutsch. Du kannst danach jedes Feld anpassen.</div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="font-display text-xl mb-2" style={{ color: "var(--wp-olive)" }}>{activeSection?.title}</div>
                    {activeSection.fields.map((f) => {
                      const v = getVal(data, activeSection.prefix, f.path);
                      const testid = `profile-field-${activeSection.prefix || "root"}-${f.path}`;
                      if (f.type === "boolean") {
                        return (
                          <label key={f.path} className="flex items-center gap-3 py-1">
                            <input
                              type="checkbox"
                              checked={v === true}
                              onChange={(e) => setData(setVal(data, activeSection.prefix, f.path, e.target.checked))}
                              data-testid={testid}
                            />
                            <span className="text-sm">{f.label}</span>
                          </label>
                        );
                      }
                      const commonProps = {
                        value: v || "",
                        onChange: (e) => setData(setVal(data, activeSection.prefix, f.path, e.target.value)),
                        placeholder: f.placeholder || "",
                        className: "w-full px-3 py-2 rounded-lg border text-sm",
                        style: { borderColor: "var(--wp-border)" },
                        "data-testid": testid,
                      };
                      return (
                        <label key={f.path} className="block">
                          <div className="text-[10px] uppercase tracking-widest opacity-60 mb-1">{f.label}</div>
                          {f.multiline ? (
                            <textarea rows={3} {...commonProps} />
                          ) : (
                            <input type="text" {...commonProps} />
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="text-[11px] opacity-60 px-4 py-2 border-t flex items-center justify-between gap-2" style={{ borderColor: "var(--wp-border)" }}>
              <span>Diese Daten füttern den PDF-Steckbrief. Nach dem Speichern kannst du das PDF neu erzeugen.</span>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
